'use client';

import type { ReactNode } from 'react';

import { Dialog, DialogClose, DialogContent } from '@ps/ui/atoms/dialog';
import { cn } from '@ps/ui/utils';
import { X } from 'lucide-react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Extra classes for the scrollable body wrapper (e.g. `gap-5`). */
  bodyClassName?: string;
  /** Body content. MUST include a `DialogTitle` for accessibility. */
  children: ReactNode;
}

/**
 * The community module's shared modal shell — a dark, neutral-surfaced dialog
 * (`bg-surface-1`) with a white circular close button pinned to the modal's
 * top-right corner and a scrollable, padded body. The close button is a direct
 * child of the (positioned) modal frame — NOT inside the scrollable body — so it
 * always sits at the frame corner regardless of body scroll.
 *
 * Consumers own the body content (creator card, info rows, CTAs, …) and MUST
 * render a `DialogTitle` inside for accessibility. Used by the channel-info
 * dialog, the subscribe blocker, and other community modals.
 */
export function CommunityModal({
  open,
  onOpenChange,
  bodyClassName,
  children,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="text-content-1 border-line bg-surface-1 dark:border-line dark:bg-surface-1 p-0 sm:max-w-md"
      >
        {/* Straddles the modal's top-right corner (negative offsets) — the frame
            has no `overflow-hidden`, so it isn't clipped. */}
        <DialogClose className="absolute -right-3 -top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-black shadow-lg transition hover:bg-neutral-200 focus:outline-none">
          <X className="h-4 w-4" aria-hidden />
          <span className="sr-only">Close</span>
        </DialogClose>

        {/* `dvh`, NOT `vh`. On mobile `vh` resolves against the LARGE viewport
            (browser chrome hidden), so a `90vh` cap can exceed what's actually
            visible: the frame is centred with `translate-y-[-50%]`, so both ends
            get clipped off-screen while this scroller — believing its content
            fits inside 90vh — produces no scrollable overflow at all. The result
            is a modal that's cut off AND unscrollable. `dvh` tracks the current
            viewport, so the cap is always reachable and real overflow scrolls. */}
        <div
          className={cn(
            'flex max-h-[90dvh] flex-col gap-4 overflow-y-auto p-6',
            bodyClassName,
          )}
        >
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
}
